import { AutoSuggest, FormGroup, Input } from '@snsw/react-component-library';
import React, { useEffect, useRef, useState } from 'react';
import { setNativeValue } from 'shared/utils/Helpers';
import { validateExt } from 'shared/utils/Validation';
import styles from './Address.module.scss';
import { countryList } from './countryList';

export const InternationalAddress = React.forwardRef(
  ({ id, onChange = () => {}, onErrorChange = () => {}, value = {}, customValidator = {}, ...rest }, ref) => {
    const {
      country: savedCountry = '',
      streetAddress: savedStreetAddress = '',
      city: savedCity = '',
      province: savedProvince = '',
      zipCode: savedZipCode = '',
    } = value;

    const addressValidator = {
      validateCountry: (country) => ({ status: true, errorMessage: '' }),
      validateStreet: (street) => ({ status: true, errorMessage: '' }),
      validateCity: (city) => ({ status: true, errorMessage: '' }),
      validateProvince: (province) => ({ status: true, errorMessage: '' }),
      validateZipCode: (zipCode) => ({ status: true, errorMessage: '' }),
      ...customValidator,
    };

    const [country, setCountry] = useState(savedCountry);
    const [streetAddress, setStreetAddress] = useState(savedStreetAddress);
    const [city, setCity] = useState(savedCity);
    const [province, setProvince] = useState(savedProvince);
    const [zipCode, setZipCode] = useState(savedZipCode);

    const [errors, setErrors] = useState({});

    const [showErrors, setShowErrors] = useState(false);
    const [errorSummary, setErrorSummary] = useState([]);

    const suggestions = countryList.filter((c) => c !== 'Australia');

    const onSelect = (suggestion, value, method) => {
      setCountry(suggestion);
    };

    const onCountryChange = (suggestion, value, method) => {
      setCountry(value);
    };

    const onBlur = (suggestion, value, highlighted) => {
      setCountry(value);
    };

    const validators = [
      {
        fieldName: `country_${id}`,
        fieldValue: country,
        isValid: addressValidator.validateCountry,
      },
      {
        fieldName: `streetAddress_${id}`,
        fieldValue: streetAddress,
        isValid: addressValidator.validateStreet,
      },
      {
        fieldName: `city_${id}`,
        fieldValue: city,
        isValid: addressValidator.validateCity,
      },
      {
        fieldName: `province_${id}`,
        fieldValue: province,
        isValid: addressValidator.validateProvince,
      },
      {
        fieldName: `zipCode_${id}`,
        fieldValue: zipCode,
        isValid: addressValidator.validateZipCode,
      },
    ];

    const countryRef = useRef();

    if (ref) {
      ref.current = {
        validate: () => {
          const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
          setShowErrors(!isCurrentComponentValid);

          return isCurrentComponentValid;
        },
      };
    }

    useEffect(() => {
      //Autosuggest cannot be initialise in current version 1.32.1
      //Use this workaround for now
      if (countryRef.current && country) {
        setNativeValue(countryRef.current, country);
        countryRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, [countryRef.current, country]);

    useEffect(() => {
      const address = {
        country,
        streetAddress,
        city,
        province,
        zipCode,
      };

      onChange(address);

      if (showErrors) validateExt(validators, setErrors, setErrorSummary);
    }, [country, streetAddress, city, province, zipCode]);

    useEffect(() => {
      onErrorChange(errorSummary);
    }, [errorSummary]);

    return (
      <>
        <AutoSuggest
          id={`country_${id}`}
          name={`country_${id}`}
          suggestions={suggestions}
          onSelect={onSelect}
          onChange={onCountryChange}
          onBlur={onBlur}
          label="Country"
          value={country}
          suggestion={country}
          className={styles.country}
          errorMessage={errors[`country_${id}`]?.errorMessage}
          hasError={showErrors && errors[`country_${id}`]?.invalid}
          inputRef={countryRef}
        />

        <FormGroup
          id={`streetAddress_${id}`}
          label="Street address"
          isRequired
          margin={{ top: 'md' }}
          errorMessage={errors[`streetAddress_${id}`]?.errorMessage}
          hasError={showErrors && errors[`streetAddress_${id}`]?.invalid}
        >
          <Input onChange={(e) => setStreetAddress(e.target.value)} value={streetAddress} />
        </FormGroup>
        <FormGroup
          id={`city_${id}`}
          label="City/town (if known)"
          margin={{ top: 'md' }}
          errorMessage={errors[`city_${id}`]?.errorMessage}
          hasError={showErrors && errors[`city_${id}`]?.invalid}
        >
          <Input onChange={(e) => setCity(e.target.value)} value={city} inputWidth="xl" />
        </FormGroup>
        <FormGroup
          id={`province_${id}`}
          label="State/province/region (if known)"
          isRequired
          margin={{ top: 'md' }}
          isOptional
          errorMessage={errors[`province_${id}`]?.errorMessage}
          hasError={showErrors && errors[`province_${id}`]?.invalid}
        >
          <Input onChange={(e) => setProvince(e.target.value)} value={province} inputWidth="xl" />
        </FormGroup>
        <FormGroup
          id={`zipCode_${id}`}
          label="Postal/zip code (if known)"
          isRequired
          margin={{ top: 'md' }}
          isOptional
          errorMessage={errors[`zipCode_${id}`]?.errorMessage}
          hasError={showErrors && errors[`zipCode_${id}`]?.invalid}
        >
          <Input onChange={(e) => setZipCode(e.target.value)} value={zipCode} inputWidth="sm" />
        </FormGroup>
      </>
    );
  },
);

InternationalAddress.displayName = 'InternationalAddress';
